import { decode } from 'jsonwebtoken';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

/**
 * Default implementation of {@link TokenDecoder}. It doesn't support decoding {@link Token} and checks the
 * expiration date from the server response.
 *
 * It's the default value on {@link Auth} constructor for {@link AuthOptions.accessTokenDecoder}.
 */
var DefaultTokenDecoder = /** @class */ (function () {
    function DefaultTokenDecoder(offsetSeconds) {
        if (offsetSeconds === void 0) { offsetSeconds = 0; }
        this.offset = offsetSeconds * 1000;
    }
    DefaultTokenDecoder.prototype.isExpired = function (token) {
        if (token.expiresAt) {
            var now = new Date().getTime() - this.offset;
            if (now >= token.expiresAt.getTime()) {
                return true;
            }
        }
        return false;
    };
    return DefaultTokenDecoder;
}());

/**
 * Default implementation of {@link TokenStorage}, delegating to Storage of the browser.
 * (`sessionStorage` or `localStorage`).
 *
 * It should be given to {@link Auth} constructor through {@link AuthOptions.tokenStorage} and
 * {@link AuthOptions.persistentTokenStorage}.
 */
var DefaultTokenStorage = /** @class */ (function () {
    function DefaultTokenStorage(storage, accessTokenStorageKey, refreshTokenStorageKey, expiresAtSuffix, credentialsTokenStorageKey) {
        if (accessTokenStorageKey === void 0) { accessTokenStorageKey = 'auth.accessToken'; }
        if (refreshTokenStorageKey === void 0) { refreshTokenStorageKey = 'auth.refreshToken'; }
        if (expiresAtSuffix === void 0) { expiresAtSuffix = '.expiresAt'; }
        if (credentialsTokenStorageKey === void 0) { credentialsTokenStorageKey = 'auth.credentials'; }
        this.async = false;
        this.storage = storage;
        this.accessTokenStorageKey = accessTokenStorageKey;
        this.refreshTokenStorageKey = refreshTokenStorageKey;
        this.expiresAtSuffix = expiresAtSuffix;
        this.credentialsTokenStorageKey = credentialsTokenStorageKey;
    }
    DefaultTokenStorage.prototype.store = function (tokens) {
        this.storage.setItem(this.accessTokenStorageKey, tokens.access.value);
        if (tokens.access.expiresAt) {
            this.storage.setItem(this.accessTokenStorageKey + this.expiresAtSuffix, tokens.access.expiresAt.getTime().toString(10));
        }
        else {
            this.storage.removeItem(this.accessTokenStorageKey + this.expiresAtSuffix);
        }
        if (tokens.refresh) {
            this.storage.setItem(this.refreshTokenStorageKey, tokens.refresh.value);
        }
        else {
            this.storage.removeItem(this.refreshTokenStorageKey);
        }
        if (tokens.refresh && tokens.refresh.expiresAt) {
            this.storage.setItem(this.refreshTokenStorageKey + this.expiresAtSuffix, tokens.refresh.expiresAt.getTime().toString(10));
        }
        else {
            this.storage.removeItem(this.refreshTokenStorageKey + this.expiresAtSuffix);
        }
        if (tokens.credentials) {
            this.storage.setItem(this.credentialsTokenStorageKey, JSON.stringify(tokens.credentials));
        }
        else {
            this.storage.removeItem(this.credentialsTokenStorageKey);
        }
    };
    DefaultTokenStorage.prototype.clear = function () {
        this.storage.removeItem(this.accessTokenStorageKey);
        this.storage.removeItem(this.refreshTokenStorageKey);
        this.storage.removeItem(this.accessTokenStorageKey + this.expiresAtSuffix);
        this.storage.removeItem(this.refreshTokenStorageKey + this.expiresAtSuffix);
        this.storage.removeItem(this.credentialsTokenStorageKey);
    };
    DefaultTokenStorage.prototype.getTokens = function () {
        var accessTokenStr = this.storage.getItem(this.accessTokenStorageKey);
        var refreshTokenStr = this.storage.getItem(this.refreshTokenStorageKey);
        var accessTokenExpiresAtStr = this.storage.getItem(this.accessTokenStorageKey + this.expiresAtSuffix);
        var refreshTokenExpiresAtStr = this.storage.getItem(this.refreshTokenStorageKey + this.expiresAtSuffix);
        var credentialsStr = this.storage.getItem(this.credentialsTokenStorageKey);
        var accessTokenExpiresAt = accessTokenExpiresAtStr ? new Date(parseInt(accessTokenExpiresAtStr, 10)) : undefined;
        var refreshTokenExpiresAt = refreshTokenExpiresAtStr
            ? new Date(parseInt(refreshTokenExpiresAtStr, 10))
            : undefined;
        var accessToken = accessTokenStr ? accessTokenStr : undefined;
        var refreshToken = refreshTokenStr ? refreshTokenStr : undefined;
        var credentials = credentialsStr ? JSON.parse(credentialsStr) : undefined;
        if (accessToken) {
            var tokens = { access: { value: accessToken, expiresAt: accessTokenExpiresAt } };
            if (refreshToken) {
                tokens.refresh = { value: refreshToken, expiresAt: refreshTokenExpiresAt };
            }
            if (credentialsStr) {
                tokens.credentials = credentials;
            }
            return tokens;
        }
    };
    return DefaultTokenStorage;
}());

var TokenStorageAsyncAdapter = /** @class */ (function () {
    function TokenStorageAsyncAdapter(tokenStorage, syncAvailable) {
        if (syncAvailable === void 0) { syncAvailable = true; }
        this.async = true;
        this.tokenStorage = tokenStorage;
        if (syncAvailable) {
            this.sync = tokenStorage;
        }
    }
    TokenStorageAsyncAdapter.prototype.clear = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.tokenStorage.clear()];
            });
        });
    };
    TokenStorageAsyncAdapter.prototype.getTokens = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.tokenStorage.getTokens()];
            });
        });
    };
    TokenStorageAsyncAdapter.prototype.store = function (tokens) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.tokenStorage.store(tokens)];
            });
        });
    };
    return TokenStorageAsyncAdapter;
}());
function toTokenStorageSync(tokenStorage) {
    if (!tokenStorage) {
        return tokenStorage;
    }
    if (!tokenStorage.async) {
        return tokenStorage;
    }
    return tokenStorage.sync;
}
function toTokenStorageAsync(tokenStorage) {
    if (!tokenStorage) {
        return tokenStorage;
    }
    if (tokenStorage.async) {
        return tokenStorage;
    }
    return new TokenStorageAsyncAdapter(tokenStorage);
}

var defaultAuthOptions = {
    accessTokenDecoder: new DefaultTokenDecoder(),
    tokenStorage: new DefaultTokenStorage(sessionStorage),
    persistentTokenStorage: new DefaultTokenStorage(localStorage),
    clientInterceptors: true
};
/**
 * Default entrypoint for auth-toolbox module.
 *
 * Load tokens from {@link TokenStorage}, support authentication methods like {@link login} and
 * {@link logout}, and automates the authentication of client provided with {@link ClientAdapter}.
 *
 * Options can be defined with {@link AuthOptions}.
 *
 * @param C Credentials type
 * @param R Client response type
 */
var Auth = /** @class */ (function () {
    /**
     * Builds an instance with given configuration.
     *
     * @param serverConfiguration Server configuration to use.
     *                            It may be a hardcoded configuration, or a configuration build from
     *                            a discovery service.
     * @param serverAdapter       Server adapter to use.
     * @param clientAdapter       Client adapter to user.
     * @param options             Options
     */
    function Auth(serverConfiguration, serverAdapter, clientAdapter, options) {
        var _this = this;
        /**
         * @inheritDoc
         */
        this.usePersistentStorage = false;
        this.listeners = [];
        this.excludes = [];
        this.interceptors = [];
        this.renewRunning = false;
        this.renewPromises = [];
        this.serverConfiguration = serverConfiguration;
        this.serverAdapter = serverAdapter;
        this.clientAdapter = clientAdapter;
        var effectiveOptions = Object.assign({}, defaultAuthOptions, options);
        this.accessTokenDecoder = effectiveOptions.accessTokenDecoder;
        this.tokenStorage = toTokenStorageSync(effectiveOptions.tokenStorage);
        this.tokenStorageAsync = toTokenStorageAsync(effectiveOptions.tokenStorage);
        this.persistentTokenStorage = toTokenStorageSync(effectiveOptions.persistentTokenStorage);
        this.persistentTokenStorageAsync = toTokenStorageAsync(effectiveOptions.persistentTokenStorage);
        if (effectiveOptions.listeners) {
            this.addListener.apply(this, effectiveOptions.listeners);
        }
        if (effectiveOptions.clientInterceptors) {
            this.initClientAdapter();
        }
        if (effectiveOptions.excludes) {
            this.addExclude.apply(this, effectiveOptions.excludes);
        }
        if (effectiveOptions.loadTokensFromStorage === undefined || effectiveOptions.loadTokensFromStorage === null) {
            // If undefined or null, tokens will be loaded only if storage supports sync loading.
            effectiveOptions.loadTokensFromStorage = this.storageSync;
        }
        if (effectiveOptions.loadTokensFromStorage) {
            if (this.storageSync) {
                this.loadTokensFromStorage();
                this.listeners.forEach(function (l) { return l.initialized && l.initialized(true); });
            }
            else {
                this.loadTokensFromStorageAsync()
                    .then(function () {
                    _this.listeners.forEach(function (l) { return l.initialized && l.initialized(true); });
                })
                    .catch(function (e) {
                    _this.listeners.forEach(function (l) { return l.initialized && l.initialized(false, e); });
                });
            }
        }
        else {
            this.listeners.forEach(function (l) { return l.initialized && l.initialized(false); });
        }
    }
    Object.defineProperty(Auth.prototype, "storageSync", {
        /**
         * @inheritDoc
         */
        get: function () {
            if (this.tokenStorageAsync && !this.tokenStorage) {
                return false;
            }
            if (this.persistentTokenStorageAsync && !this.persistentTokenStorage) {
                return false;
            }
            return true;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @inheritDoc
     */
    Auth.prototype.loadTokensFromStorageAsync = function () {
        return __awaiter(this, void 0, void 0, function () {
            var tokens, persistentTokens;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.tokenStorageAsync) return [3 /*break*/, 11];
                        return [4 /*yield*/, this.tokenStorageAsync.getTokens()];
                    case 1:
                        tokens = _a.sent();
                        if (!this.persistentTokenStorageAsync) return [3 /*break*/, 6];
                        return [4 /*yield*/, this.persistentTokenStorageAsync.getTokens()];
                    case 2:
                        persistentTokens = _a.sent();
                        if (persistentTokens) {
                            this.usePersistentStorage = true;
                        }
                        if (!tokens) {
                            tokens = persistentTokens;
                        }
                        if (!!tokens) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.persistentTokenStorageAsync.clear()];
                    case 3:
                        _a.sent();
                        return [3 /*break*/, 6];
                    case 4: return [4 /*yield*/, this.persistentTokenStorageAsync.store(tokens)];
                    case 5:
                        _a.sent();
                        _a.label = 6;
                    case 6:
                        if (!!tokens) return [3 /*break*/, 8];
                        return [4 /*yield*/, this.unsetTokensImplAsync()];
                    case 7:
                        _a.sent();
                        return [3 /*break*/, 10];
                    case 8: return [4 /*yield*/, this.setTokensImplAsync(tokens)];
                    case 9:
                        _a.sent();
                        _a.label = 10;
                    case 10: return [2 /*return*/, tokens];
                    case 11: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.loadTokensFromStorage = function () {
        if (this.tokenStorageAsync) {
            if (!this.tokenStorage) {
                throw new Error('tokenStorage is async. Use loadTokensFromStorageAsync method instead');
            }
            var tokens = this.tokenStorage.getTokens();
            if (this.persistentTokenStorageAsync) {
                if (!this.persistentTokenStorage) {
                    throw new Error('persistentTokenStorage is async. Use loadTokensFromStorageAsync method instead');
                }
                var persistentTokens = this.persistentTokenStorage.getTokens();
                if (persistentTokens) {
                    this.usePersistentStorage = true;
                }
                if (!tokens) {
                    tokens = persistentTokens;
                }
                if (!tokens) {
                    this.persistentTokenStorage.clear();
                }
                else {
                    this.persistentTokenStorage.store(tokens);
                }
            }
            if (!tokens) {
                this.unsetTokensImpl();
            }
            else {
                this.setTokensImpl(tokens);
            }
            return tokens;
        }
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.release = function () {
        for (var _i = 0, _a = this.interceptors; _i < _a.length; _i++) {
            var handle = _a[_i];
            handle();
        }
        this.removeListener.apply(this, this.listeners);
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.addListener = function () {
        var listeners = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            listeners[_i] = arguments[_i];
        }
        var _a;
        (_a = this.listeners).push.apply(_a, listeners);
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.removeListener = function () {
        var listeners = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            listeners[_i] = arguments[_i];
        }
        for (var _a = 0, listeners_1 = listeners; _a < listeners_1.length; _a++) {
            var listener = listeners_1[_a];
            var indexOf = this.listeners.indexOf(listener);
            if (indexOf > -1) {
                this.listeners.splice(indexOf, 1);
            }
        }
    };
    Auth.prototype.addExclude = function () {
        var excludes = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            excludes[_i] = arguments[_i];
        }
        var _loop_1 = function (exclude) {
            var excludeFunction = void 0;
            if (typeof exclude === 'string') {
                excludeFunction = function (request) { return request.url === exclude; };
            }
            else if (exclude instanceof RegExp) {
                excludeFunction = function (request) { return exclude.test(request.url); };
            }
            else {
                excludeFunction = exclude;
            }
            this_1.excludes.push(excludeFunction);
        };
        var this_1 = this;
        for (var _a = 0, excludes_1 = excludes; _a < excludes_1.length; _a++) {
            var exclude = excludes_1[_a];
            _loop_1(exclude);
        }
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.getTokens = function () {
        return this.tokens;
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.decodeAccessToken = function () {
        if (this.tokens) {
            if (!this.accessTokenDecoder || !this.accessTokenDecoder.decode) {
                throw new Error('An accessTokenDecoder supporting decode method should be defined to decode access token.');
            }
            return this.accessTokenDecoder.decode(this.tokens.access);
        }
    };
    Auth.prototype.isExpiredAccessToken = function (offset) {
        if (this.tokens) {
            if (!this.accessTokenDecoder || !this.accessTokenDecoder.isExpired) {
                throw new Error('An accessTokenDecoder supporting isExpired method should be defined to check if access token is expired.');
            }
            return this.accessTokenDecoder.isExpired(this.tokens.access, offset);
        }
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.isAuthenticated = function () {
        return !!this.tokens;
    };
    Auth.prototype.getServerConfiguration = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!!this.deferredServerConfiguration) return [3 /*break*/, 2];
                        _a = this;
                        return [4 /*yield*/, this.serverConfiguration];
                    case 1:
                        _a.deferredServerConfiguration = _b.sent();
                        _b.label = 2;
                    case 2: return [2 /*return*/, this.deferredServerConfiguration];
                }
            });
        });
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.login = function (credentials) {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.loginImpl(credentials)];
                    case 1:
                        response = _a.sent();
                        this.listeners.forEach(function (l) { return l.login && l.login(); });
                        return [2 /*return*/, response];
                }
            });
        });
    };
    Auth.prototype.loginImpl = function (credentials) {
        return __awaiter(this, void 0, void 0, function () {
            var serverConfiguration, request, response, tokens;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getServerConfiguration()];
                    case 1:
                        serverConfiguration = _a.sent();
                        request = this.serverAdapter.asLoginRequest(serverConfiguration.loginEndpoint, credentials);
                        return [4 /*yield*/, this.clientAdapter.login(request)];
                    case 2:
                        response = _a.sent();
                        tokens = this.serverAdapter.getResponseTokens(response);
                        if (this.usePersistentStorage && !serverConfiguration.renewEndpoint) {
                            tokens.credentials = credentials;
                        }
                        return [4 /*yield*/, this.setTokensImplAsync(tokens)];
                    case 3:
                        _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.renew = function () {
        return __awaiter(this, void 0, void 0, function () {
            var serverConfiguration, response, request, tokens, _i, _a, renewTokenPromise, err_1, _b, _c, renewTokenPromise, renewTokenPromise_1;
            var _this = this;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0: return [4 /*yield*/, this.getServerConfiguration()];
                    case 1:
                        serverConfiguration = _d.sent();
                        if (!(this.tokens && serverConfiguration)) return [3 /*break*/, 12];
                        if (!!this.renewRunning) return [3 /*break*/, 11];
                        _d.label = 2;
                    case 2:
                        _d.trys.push([2, 8, 9, 10]);
                        this.renewRunning = true;
                        response = void 0;
                        if (!serverConfiguration.renewEndpoint) return [3 /*break*/, 4];
                        if (!this.tokens.refresh) {
                            throw new Error('No refresh token available to renew');
                        }
                        request = this.serverAdapter.asRenewRequest(serverConfiguration.renewEndpoint, this.tokens.refresh);
                        return [4 /*yield*/, this.clientAdapter.renew(request)];
                    case 3:
                        response = _d.sent();
                        return [3 /*break*/, 6];
                    case 4:
                        if (!this.tokens.credentials) {
                            throw new Error('Credentials are not available. ' +
                                'usePersistentStorage should be true to allow renew method without renewEndpoint and refresh token.');
                        }
                        return [4 /*yield*/, this.loginImpl(this.tokens.credentials)];
                    case 5:
                        response = _d.sent();
                        _d.label = 6;
                    case 6:
                        tokens = this.serverAdapter.getResponseTokens(response);
                        return [4 /*yield*/, this.setTokensImplAsync(tokens)];
                    case 7:
                        _d.sent();
                        for (_i = 0, _a = this.renewPromises; _i < _a.length; _i++) {
                            renewTokenPromise = _a[_i];
                            renewTokenPromise.resolve(response);
                        }
                        this.listeners.forEach(function (l) { return l.renew && l.renew(); });
                        return [2 /*return*/, response];
                    case 8:
                        err_1 = _d.sent();
                        for (_b = 0, _c = this.renewPromises; _b < _c.length; _b++) {
                            renewTokenPromise = _c[_b];
                            renewTokenPromise.reject(err_1);
                        }
                        throw err_1;
                    case 9:
                        this.renewRunning = false;
                        this.renewPromises = [];
                        return [7 /*endfinally*/];
                    case 10: return [3 /*break*/, 12];
                    case 11:
                        renewTokenPromise_1 = new Promise(function (resolve, reject) {
                            _this.renewPromises.push({ promise: renewTokenPromise_1, resolve: resolve, reject: reject });
                        });
                        return [2 /*return*/, renewTokenPromise_1];
                    case 12: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.logout = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, serverConfiguration, request;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.tokens) return [3 /*break*/, 5];
                        response = void 0;
                        return [4 /*yield*/, this.getServerConfiguration()];
                    case 1:
                        serverConfiguration = _a.sent();
                        if (!(serverConfiguration.logoutEndpoint && this.tokens.refresh)) return [3 /*break*/, 3];
                        request = this.serverAdapter.asLogoutRequest(serverConfiguration.logoutEndpoint, this.tokens.refresh);
                        return [4 /*yield*/, this.clientAdapter.logout(request)];
                    case 2:
                        response = _a.sent();
                        _a.label = 3;
                    case 3: return [4 /*yield*/, this.unsetTokensImplAsync()];
                    case 4:
                        _a.sent();
                        this.listeners.forEach(function (l) { return l.logout && l.logout(); });
                        return [2 /*return*/, response];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    Auth.prototype.initClientAdapter = function () {
        this.interceptors.push(this.clientAdapter.setupRequestInterceptor(this));
        this.interceptors.push(this.clientAdapter.setupErrorResponseInterceptor(this));
    };
    Auth.prototype.isLoginRequest = function (request) {
        return __awaiter(this, void 0, void 0, function () {
            var serverConfiguration;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getServerConfiguration()];
                    case 1:
                        serverConfiguration = _a.sent();
                        if (serverConfiguration.loginEndpoint &&
                            serverConfiguration.loginEndpoint.method.toLowerCase() === request.method.toLowerCase() &&
                            serverConfiguration.loginEndpoint.url === request.url) {
                            return [2 /*return*/, true];
                        }
                        return [2 /*return*/, false];
                }
            });
        });
    };
    Auth.prototype.isRenewRequest = function (request) {
        return __awaiter(this, void 0, void 0, function () {
            var serverConfiguration;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getServerConfiguration()];
                    case 1:
                        serverConfiguration = _a.sent();
                        if (serverConfiguration.renewEndpoint &&
                            serverConfiguration.renewEndpoint.method.toLowerCase() === request.method.toLowerCase() &&
                            serverConfiguration.renewEndpoint.url === request.url) {
                            return [2 /*return*/, true];
                        }
                        return [2 /*return*/, false];
                }
            });
        });
    };
    Auth.prototype.expired = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.tokens) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.unsetTokensImplAsync()];
                    case 1:
                        _a.sent();
                        this.listeners.forEach(function (l) { return l.expired && l.expired(); });
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    Auth.prototype.unsetTokensImplAsync = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.tokens = undefined;
                        if (!this.persistentTokenStorageAsync) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.persistentTokenStorageAsync.clear()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        if (!this.tokenStorageAsync) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.tokenStorageAsync.clear()];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4:
                        this.listeners.forEach(function (l) { return l.tokensChanged && l.tokensChanged(); });
                        return [2 /*return*/];
                }
            });
        });
    };
    Auth.prototype.setTokensImplAsync = function (tokens) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.tokenStorageAsync) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.tokenStorageAsync.store(tokens)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        if (!this.persistentTokenStorageAsync) return [3 /*break*/, 6];
                        if (!this.usePersistentStorage) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.persistentTokenStorageAsync.store(tokens)];
                    case 3:
                        _a.sent();
                        return [3 /*break*/, 6];
                    case 4: return [4 /*yield*/, this.persistentTokenStorageAsync.clear()];
                    case 5:
                        _a.sent();
                        _a.label = 6;
                    case 6:
                        this.tokens = tokens;
                        this.listeners.forEach(function (l) { return l.tokensChanged && l.tokensChanged(_this.tokens); });
                        return [2 /*return*/];
                }
            });
        });
    };
    Auth.prototype.unsetTokensImpl = function () {
        if (this.tokenStorageAsync) {
            if (!this.tokenStorage) {
                throw new Error('tokenStorage is async. Use setTokensAsync method instead');
            }
            this.tokenStorage.clear();
        }
        if (this.persistentTokenStorageAsync) {
            if (!this.persistentTokenStorage) {
                throw new Error('persistentTokenStorage is async. Use setTokensAsync method instead');
            }
            this.persistentTokenStorage.clear();
        }
        this.tokens = undefined;
        this.listeners.forEach(function (l) { return l.tokensChanged && l.tokensChanged(); });
    };
    Auth.prototype.setTokensImpl = function (tokens) {
        var _this = this;
        if (this.tokenStorageAsync) {
            if (!this.tokenStorage) {
                throw new Error('tokenStorage is async. Use setTokensAsync method instead');
            }
            this.tokenStorage.store(tokens);
        }
        if (this.persistentTokenStorageAsync) {
            if (!this.persistentTokenStorage) {
                throw new Error('persistentTokenStorage is async. Use setTokensAsync method instead');
            }
            if (this.usePersistentStorage) {
                this.persistentTokenStorage.store(tokens);
            }
            else {
                this.persistentTokenStorage.clear();
            }
        }
        this.tokens = tokens;
        this.listeners.forEach(function (l) { return l.tokensChanged && l.tokensChanged(_this.tokens); });
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.setTokens = function (tokens) {
        if (tokens) {
            this.setTokensImpl(tokens);
        }
        else {
            this.unsetTokensImpl();
        }
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.setTokensAsync = function (tokens) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!tokens) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.setTokensImplAsync(tokens)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, this.unsetTokensImplAsync()];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    Auth.prototype.isExcluded = function (request, response) {
        for (var _i = 0, _a = this.excludes; _i < _a.length; _i++) {
            var exclude = _a[_i];
            if (exclude(request, response)) {
                return true;
            }
        }
        return false;
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.interceptRequest = function (request) {
        return __awaiter(this, void 0, void 0, function () {
            var tokens, isLoginRequest, isRenewRequest, err_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.isExcluded(request)) {
                            return [2 /*return*/, false];
                        }
                        tokens = this.getTokens();
                        return [4 /*yield*/, this.isLoginRequest(request)];
                    case 1:
                        isLoginRequest = _a.sent();
                        if (!(tokens && tokens.access && !isLoginRequest)) return [3 /*break*/, 11];
                        if (!tokens.refresh) return [3 /*break*/, 9];
                        return [4 /*yield*/, this.isRenewRequest(request)];
                    case 2:
                        isRenewRequest = _a.sent();
                        if (!(!isRenewRequest &&
                            this.accessTokenDecoder &&
                            this.accessTokenDecoder.isExpired &&
                            this.accessTokenDecoder.isExpired(tokens.access))) return [3 /*break*/, 8];
                        _a.label = 3;
                    case 3:
                        _a.trys.push([3, 5, , 7]);
                        return [4 /*yield*/, this.renew()];
                    case 4:
                        _a.sent();
                        return [3 /*break*/, 7];
                    case 5:
                        err_2 = _a.sent();
                        return [4 /*yield*/, this.expired()];
                    case 6:
                        _a.sent();
                        throw err_2;
                    case 7:
                        tokens = this.getTokens();
                        _a.label = 8;
                    case 8:
                        this.serverAdapter.setAccessToken(request, tokens.access.value);
                        return [2 /*return*/, true];
                    case 9:
                        this.serverAdapter.setAccessToken(request, tokens.access.value);
                        if (!(this.accessTokenDecoder &&
                            this.accessTokenDecoder.isExpired &&
                            this.accessTokenDecoder.isExpired(tokens.access))) return [3 /*break*/, 11];
                        return [4 /*yield*/, this.expired()];
                    case 10:
                        _a.sent();
                        throw new Error('Access token is expired');
                    case 11: return [2 /*return*/, false];
                }
            });
        });
    };
    /**
     * @inheritDoc
     */
    Auth.prototype.interceptResponse = function (request, response) {
        return __awaiter(this, void 0, void 0, function () {
            var tokens, refreshToken, isRenewRequest, err_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.isExcluded(request, response)) {
                            return [2 /*return*/, false];
                        }
                        tokens = this.getTokens();
                        refreshToken = tokens && tokens.refresh ? tokens.refresh.value : undefined;
                        if (!refreshToken) return [3 /*break*/, 10];
                        return [4 /*yield*/, this.isRenewRequest(request)];
                    case 1:
                        isRenewRequest = _a.sent();
                        if (!(!isRenewRequest && this.serverAdapter.accessTokenHasExpired(request, response))) return [3 /*break*/, 7];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 6]);
                        return [4 /*yield*/, this.renew()];
                    case 3:
                        _a.sent();
                        return [2 /*return*/, true];
                    case 4:
                        err_3 = _a.sent();
                        return [4 /*yield*/, this.expired()];
                    case 5:
                        _a.sent();
                        return [2 /*return*/, false];
                    case 6: return [3 /*break*/, 9];
                    case 7:
                        if (!(isRenewRequest && this.serverAdapter.refreshTokenHasExpired(request, response))) return [3 /*break*/, 9];
                        return [4 /*yield*/, this.expired()];
                    case 8:
                        _a.sent();
                        return [2 /*return*/, false];
                    case 9: return [2 /*return*/, false];
                    case 10:
                        if (!this.serverAdapter.accessTokenHasExpired(request, response)) return [3 /*break*/, 12];
                        return [4 /*yield*/, this.expired()];
                    case 11:
                        _a.sent();
                        _a.label = 12;
                    case 12: return [2 /*return*/, false];
                }
            });
        });
    };
    return Auth;
}());

/**
 * Wraps instance of Axios client into a {@link ClientAdapter} to support automated authentication.
 *
 * It should be given to {@link Auth} constructor.
 */
var AxiosAdapter = /** @class */ (function () {
    function AxiosAdapter(axios, config) {
        if (config === void 0) { config = {}; }
        this.axios = axios;
        this.config = config;
    }
    AxiosAdapter.prototype.login = function (request) {
        return this.axios.request(__assign({}, this.config, request));
    };
    AxiosAdapter.prototype.logout = function (request) {
        return this.axios.request(__assign({}, this.config, request));
    };
    AxiosAdapter.prototype.renew = function (request) {
        return this.axios.request(__assign({}, this.config, request));
    };
    AxiosAdapter.prototype.request = function (request) {
        return this.axios.request(__assign({}, request));
    };
    AxiosAdapter.prototype.asResponse = function (response) {
        var r = {};
        if (response.data) {
            r.data = response.data;
        }
        if (response.headers) {
            r.headers = response.headers;
        }
        if (response.status) {
            r.status = response.status;
        }
        return r;
    };
    AxiosAdapter.prototype.asRequest = function (request) {
        if (!request.url)
            throw new Error('No url is defined');
        if (!request.method)
            throw new Error('No method is defined');
        var r = { url: request.url, method: request.method };
        if (request.data) {
            r.data = request.data;
        }
        if (request.headers) {
            r.headers = request.headers;
        }
        return r;
    };
    AxiosAdapter.prototype.setupRequestInterceptor = function (interceptor) {
        var _this = this;
        var id = this.axios.interceptors.request.use(function (config) { return __awaiter(_this, void 0, void 0, function () {
            var request, intercepted;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        request = this.asRequest(config);
                        return [4 /*yield*/, interceptor.interceptRequest(request)];
                    case 1:
                        intercepted = _a.sent();
                        if (intercepted) {
                            config.data = request.data;
                            config.headers = request.headers;
                        }
                        return [2 /*return*/, config];
                }
            });
        }); });
        return function () { return _this.axios.interceptors.request.eject(id); };
    };
    AxiosAdapter.prototype.setupErrorResponseInterceptor = function (interceptor) {
        var _this = this;
        var id = this.axios.interceptors.response.use(function (response) { return response; }, function (error) { return __awaiter(_this, void 0, void 0, function () {
            var request, response, intercepted;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!error.response) return [3 /*break*/, 2];
                        request = this.asRequest(error.config);
                        response = this.asResponse(error.response);
                        return [4 /*yield*/, interceptor.interceptResponse(request, response)];
                    case 1:
                        intercepted = _a.sent();
                        if (intercepted) {
                            error.config.baseURL = undefined; // Workaround
                            return [2 /*return*/, this.axios.request(error.config)];
                        }
                        _a.label = 2;
                    case 2: throw error;
                }
            });
        }); });
        return function () { return _this.axios.interceptors.response.eject(id); };
    };
    return AxiosAdapter;
}());

/**
 * @file Automatically generated by barrelsby.
 */

// Copyright Joyent, Inc. and other Node contributors.
var isArray = Array.isArray || function (xs) {
  return Object.prototype.toString.call(xs) === '[object Array]';
};
function stringifyPrimitive(v) {
  switch (typeof v) {
    case 'string':
      return v;

    case 'boolean':
      return v ? 'true' : 'false';

    case 'number':
      return isFinite(v) ? v : '';

    default:
      return '';
  }
}

function stringify (obj, sep, eq, name) {
  sep = sep || '&';
  eq = eq || '=';
  if (obj === null) {
    obj = undefined;
  }

  if (typeof obj === 'object') {
    return map(objectKeys(obj), function(k) {
      var ks = encodeURIComponent(stringifyPrimitive(k)) + eq;
      if (isArray(obj[k])) {
        return map(obj[k], function(v) {
          return ks + encodeURIComponent(stringifyPrimitive(v));
        }).join(sep);
      } else {
        return ks + encodeURIComponent(stringifyPrimitive(obj[k]));
      }
    }).join(sep);

  }

  if (!name) return '';
  return encodeURIComponent(stringifyPrimitive(name)) + eq +
         encodeURIComponent(stringifyPrimitive(obj));
}
function map (xs, f) {
  if (xs.map) return xs.map(f);
  var res = [];
  for (var i = 0; i < xs.length; i++) {
    res.push(f(xs[i], i));
  }
  return res;
}

var objectKeys = Object.keys || function (obj) {
  var res = [];
  for (var key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) res.push(key);
  }
  return res;
};

/**
 * Provides a {@link ServerConfiguration} through [OpenID discovery](https://openid.net/specs/openid-connect-discovery-1_0.html).
 *
 * It requests `{issuerUrl}/.well-known/openid-configuration` to retrieve login, renew and logout
 * endpoints.
 *
 * It should be given to {@link Auth} constructor.
 *
 * @param client client adapter to request discovery endpoint with.
 * @param issuerUrl url of the authentication realm (without `/.well-known/openid-configuration`).
 */
function openidConnectDiscovery(client, issuerUrl) {
    return __awaiter(this, void 0, void 0, function () {
        var clientResponse, response, openidConfiguration;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, client.request({
                        method: 'GET',
                        url: issuerUrl + '/.well-known/openid-configuration'
                    })];
                case 1:
                    clientResponse = _a.sent();
                    response = client.asResponse(clientResponse);
                    openidConfiguration = response.data;
                    return [2 /*return*/, {
                            loginEndpoint: { method: 'POST', url: openidConfiguration.token_endpoint },
                            renewEndpoint: { method: 'POST', url: openidConfiguration.token_endpoint },
                            logoutEndpoint: { method: 'POST', url: openidConfiguration.end_session_endpoint }
                        }];
            }
        });
    });
}
/**
 * Implements OpenID Connect "Resource Owner Password Credentials Grant", it should be given to
 * {@link Auth} constructor.
 */
var OpenidConnectAdapter = /** @class */ (function () {
    function OpenidConnectAdapter() {
    }
    OpenidConnectAdapter.prototype.asLoginRequest = function (loginEndpoint, credentials) {
        var rawData = {
            grant_type: 'password',
            username: credentials.username,
            password: credentials.password
        };
        var data = stringify(rawData);
        var headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        return __assign({}, loginEndpoint, { data: data, headers: headers });
    };
    OpenidConnectAdapter.prototype.asLogoutRequest = function (logoutEndpoint, refreshToken) {
        var rawData = {
            refresh_token: refreshToken.value
        };
        var data = stringify(rawData);
        var headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        return __assign({}, logoutEndpoint, { data: data, headers: headers });
    };
    OpenidConnectAdapter.prototype.asRenewRequest = function (renewEndpoint, refreshToken) {
        var rawData = {
            grant_type: 'refresh_token',
            refresh_token: refreshToken.value
        };
        var data = stringify(rawData);
        var headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
        return __assign({}, renewEndpoint, { data: data, headers: headers });
    };
    OpenidConnectAdapter.prototype.getResponseTokens = function (response) {
        var loginResponse = response.data;
        if (!loginResponse.access_token) {
            throw new Error('No access token found in response');
        }
        var tokens = {
            access: { value: loginResponse.access_token }
        };
        if (loginResponse.expires_in) {
            var expiresAt = new Date();
            expiresAt.setSeconds(expiresAt.getSeconds() + loginResponse.expires_in);
            tokens.access.expiresAt = expiresAt;
        }
        if (loginResponse.refresh_token) {
            tokens.refresh = { value: loginResponse.refresh_token };
            if (loginResponse.refresh_expires_in) {
                var expiresAt = new Date();
                expiresAt.setSeconds(expiresAt.getSeconds() + loginResponse.refresh_expires_in);
                tokens.refresh.expiresAt = expiresAt;
            }
        }
        return tokens;
    };
    OpenidConnectAdapter.prototype.setAccessToken = function (request, accessToken) {
        if (!request.headers) {
            request.headers = {};
        }
        request.headers.Authorization = 'Bearer ' + accessToken;
    };
    OpenidConnectAdapter.prototype.accessTokenHasExpired = function (request, response) {
        return (!!request.headers &&
            !!request.headers.Authorization &&
            response.status === 401 &&
            response.data.error === 'invalid_token');
    };
    OpenidConnectAdapter.prototype.refreshTokenHasExpired = function (request, response) {
        return (!!request.headers &&
            !!request.headers.Authorization &&
            response.status === 400 &&
            response.data.error === 'invalid_grant');
    };
    return OpenidConnectAdapter;
}());

/**
 * @file Automatically generated by barrelsby.
 */

/**
 * {@link TokenDecoder} based on jsonwebtoken package to be used with JWT {@link Token}.
 * It check expiration dates from both server response and JWT `exp` claim, and supports decoding
 * of the JWT token.
 *
 * It should be given to {@link Auth} constructor through {@link AuthOptions.accessTokenDecoder}.
 */
var JwtTokenDecoder = /** @class */ (function (_super) {
    __extends(JwtTokenDecoder, _super);
    /**
     * @param expiredOffset Offset in millisecond to consider the token as expired.
     *                      use NaN to disable expiration from token decoding
     */
    function JwtTokenDecoder(expiredOffset) {
        if (expiredOffset === void 0) { expiredOffset = 0; }
        return _super.call(this, expiredOffset) || this;
    }
    JwtTokenDecoder.prototype.decode = function (token) {
        return decode(token.value);
    };
    JwtTokenDecoder.prototype.isExpired = function (token, offset) {
        if (_super.prototype.isExpired.call(this, token)) {
            return true;
        }
        if (offset === undefined) {
            offset = this.offset;
        }
        if (isNaN(offset)) {
            return false;
        }
        var now = Math.round((new Date().getTime() - offset) / 1000);
        var decoded = this.decode(token);
        if ('exp' in decoded && now >= decoded.exp) {
            return true;
        }
        return false;
    };
    return JwtTokenDecoder;
}(DefaultTokenDecoder));

/**
 * @file Automatically generated by barrelsby.
 */

/**
 * @file Automatically generated by barrelsby.
 */

/**
 * @file Automatically generated by barrelsby.
 */

export { Auth, AxiosAdapter, openidConnectDiscovery, OpenidConnectAdapter, DefaultTokenDecoder, JwtTokenDecoder, TokenStorageAsyncAdapter, toTokenStorageSync, toTokenStorageAsync, DefaultTokenStorage };
//# sourceMappingURL=auth-toolbox.esm.js.map
